import { api } from "./api";

/**
 * Editor-managed page content.
 *
 * `storefront_sections` has been in the database, editable in admin and served
 * publicly at `/homepage-sections?placement=…` since it was built — with no
 * consumer anywhere. `placement` is an unconstrained string, so a page is just
 * the ordered, visible sections filed under its own placement. That is what
 * these pages read.
 */
const cache = new Map();
const inFlight = new Map();
const TTL_MS = 5 * 60 * 1000;

export function getSections(placement) {
  const key = String(placement || "home");
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < TTL_MS) return Promise.resolve(hit.data);
  if (inFlight.has(key)) return inFlight.get(key);

  const request = api.get(`/homepage-sections?placement=${encodeURIComponent(key)}`)
    .then((response) => {
      const rows = Array.isArray(response?.data) ? response.data : [];
      cache.set(key, { data: rows, at: Date.now() });
      return rows;
    })
    .catch(() => [])
    .finally(() => { inFlight.delete(key); });

  inFlight.set(key, request);
  return request;
}

/**
 * The company's own details — address, phone, email, hours, socials.
 *
 * Kept in the `settings` bag of a single section rather than a new table: the
 * column is free-form JSONB and already admin-editable, so this needed no
 * schema change. Before this, none of it existed anywhere — `policies.js` held
 * `contactEmail: null, contactPhone: null, atelierAddress: null` with a comment
 * asking the owner to supply them.
 */
export const CONTACT_DETAILS_KEY = "contact-details";

export function getContactDetails() {
  return getSections("contact").then((rows) => {
    const row = rows.find((section) => section.key === CONTACT_DETAILS_KEY);
    return row?.settings && typeof row.settings === "object" ? row.settings : {};
  });
}

export default getSections;

/** The boutiques, as the admin panel lists them — visible rows only. */
export function getStoreLocations() {
  const key = "__stores__";
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < TTL_MS) return Promise.resolve(hit.data);
  if (inFlight.has(key)) return inFlight.get(key);
  const request = api.get("/store-locations")
    .then((response) => {
      const rows = Array.isArray(response?.data) ? response.data : [];
      cache.set(key, { data: rows, at: Date.now() });
      return rows;
    })
    .catch(() => [])
    .finally(() => { inFlight.delete(key); });
  inFlight.set(key, request);
  return request;
}
